import React, { useMemo } from 'react';
import styles from './NewClient.module.scss';
import useWindowSize from 'utils/useWindowSize';
import { useDispatch } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import TextField from '../../../Common/TextField';
import AvatarIcon from '../../../Common/AvatarIcon';
import * as yup from 'yup';
import clx from 'classnames';
import { clientActions } from 'Containers/Clients/store';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const NewClientModal = ({ salone, closeModal }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().trim().required(t('clients.newClientModal.fields.name.required')),
        surname: yup
          .string()
          .trim()
          .required(t('clients.newClientModal.fields.surname.required')),
        email: yup.string().trim().email(t('clients.newClientModal.fields.email.valid')),
        cellulare: yup
          .string()
          .trim()
          .test(
            'phone',
            t('clients.newClientModal.fields.cellulare.valid'),
            (value) => !value || phoneRegExp.test(value),
          ),
      }),
    [t],
  );

  const initialValues = {
    salone: salone?.idsalone,
    name: '',
    surname: '',
    email: '',
    cellulare: '',
  };

  const validateContact = (values) => {
    const errors = {};
    if (!values.email?.trim() && !values.cellulare?.trim()) {
      errors.contact = t('clients.newClientModal.errors.atLeastOne');
    }
    return errors;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validateContact}
      validateOnBlur
      validateOnChange={false}
      onSubmit={(values, { setSubmitting }) => {
        const req = {
          salone: values.salone,
          nome: values.name.trim(),
          cognome: values.surname.trim(),
          email: values.email?.trim() || '',
          cellulare: values.cellulare?.trim() || '',
        };

        dispatch(
          clientActions.ADD_CLIENT.REQUEST(req, () => {
            setSubmitting(false);
            closeModal?.();
          }),
        );

        window.setTimeout(() => setSubmitting(false), 6000);
      }}
    >
      {({ values, errors, touched, isSubmitting, submitCount }) => {
        const previewName = `${values.name || ''} ${values.surname || ''}`.trim();
        const showContactError =
          (submitCount > 0 || touched.email || touched.cellulare) && errors.contact;

        return (
          <Form
            className={clx(styles.client_modal_wrapper, {
              [styles.client_modal_wrapper_mobile]: isMobile,
            })}
          >
            <div className={styles.preview}>
              <AvatarIcon name={previewName || '?'} size="56px" />
              <div className={styles.preview_copy}>
                <span className={styles.preview_name}>
                  {previewName || t('clients.newClientModal.previewPlaceholder')}
                </span>
                <span className={styles.preview_hint}>
                  {t('clients.newClientModal.previewHint')}
                </span>
              </div>
            </div>

            <div className={styles.body}>
              <div className={styles.section_label}>
                <UserPlus size={14} strokeWidth={2.4} aria-hidden="true" />
                {t('clients.newClientModal.sections.identity')}
              </div>
              <div className={styles.fields_row}>
                <Field
                  name="name"
                  id="name"
                  label={t('clients.newClientModal.fields.name.label')}
                  component={TextField}
                  height="48px"
                  margin="0"
                  placeholder={t('clients.newClientModal.fields.name.placeholder')}
                  autoComplete="given-name"
                />
                <Field
                  name="surname"
                  id="surname"
                  label={t('clients.newClientModal.fields.surname.label')}
                  component={TextField}
                  height="48px"
                  margin="0"
                  placeholder={t('clients.newClientModal.fields.surname.placeholder')}
                  autoComplete="family-name"
                />
              </div>

              <div className={styles.section_label}>
                {t('clients.newClientModal.sections.contact')}
              </div>
              <p className={styles.section_hint}>{t('clients.newClientModal.contactHint')}</p>
              {showContactError ? (
                <span className={styles.error_text} role="alert">
                  {errors.contact}
                </span>
              ) : null}
              <Field
                name="email"
                id="email"
                type="email"
                label={t('clients.newClientModal.fields.email.label')}
                component={TextField}
                height="48px"
                margin="0 0 14px 0"
                placeholder={t('clients.newClientModal.fields.email.placeholder')}
                autoComplete="email"
                showIcon
              />
              <Field
                name="cellulare"
                id="cellulare"
                type="tel"
                label={t('clients.newClientModal.fields.cellulare.label')}
                component={TextField}
                height="48px"
                margin="0"
                placeholder={t('clients.newClientModal.fields.cellulare.placeholder')}
                autoComplete="tel"
              />
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.cancel_button}
                onClick={() => closeModal?.()}
                disabled={isSubmitting}
              >
                {t('common.actions.cancel')}
              </button>
              <button type="submit" className={styles.confirm_button} disabled={isSubmitting}>
                {isSubmitting
                  ? t('clients.newClientModal.submitting')
                  : t('clients.newClientModal.submit')}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default NewClientModal;
